import {sendCustomMetric, sendStats} from '@github-ui/stats'
import {getCurrentReactAppName} from '@github-ui/stats-metadata'
import {type PropsWithChildren, useEffect, useEffectEvent, version} from 'react'

const REACT_TRACK_DETAIL = {
  devtools: {
    dataType: 'track-entry' as const,
    track: 'React root',
    trackGroup: 'Performance Timeline',
    color: 'primary' as const,
  },
}

function reactTrackDetail(tooltipText: string, color?: string) {
  return {
    detail: {
      devtools: {
        ...REACT_TRACK_DETAIL.devtools,
        tooltipText,
        ...(color ? {color} : {}),
      },
    },
  }
}

function safeMeasureWithStartMark(name: string, startMarkName: string, detail: ReturnType<typeof reactTrackDetail>) {
  try {
    performance.measure(name, {
      start: startMarkName,
      detail: detail.detail,
    })
  } catch {
    // Missing marks can happen during interrupted/torn-down renders; avoid surfacing instrumentation failures.
  }
}

export interface ReactRootProfilingState {
  enabled: boolean
  source: 'server' | 'client-fallback' | 'not-enabled'
}

export interface ReactRootRenderStats {
  csr: boolean
  ssr: boolean
  ssrError: boolean
}

/**
 * Tracks one React root from startup through its initial commit.
 *
 * Profiling state is supplied by each runtime so this low-level tracker does
 * not add profiler configuration to startup bundles that do not use it.
 */
export function createReactRootPerformanceTracker({
  appName,
  renderType,
  profiling,
}: {
  appName: string
  renderType: 'createRoot' | 'hydrateRoot'
  profiling: ReactRootProfilingState
}) {
  const rootStartMarkName = `react-root:start:${appName}`
  const renderStartMarkName = `react-root:render-start:${appName}`
  const commitMarkName = `react-root:commit:${appName}`

  try {
    performance.mark(rootStartMarkName)
  } catch {
    // Instrumentation must not prevent the root from rendering.
  }

  let didStartRender = false
  let didRecordRender = false
  let didCommit = false
  let collectRenderPerformanceStats = () => {}

  const startRender = () => {
    if (didStartRender) return
    didStartRender = true

    let renderStartTime = performance.now()
    try {
      // performance.mark() can return undefined in some older browser engines.
      renderStartTime = performance.mark(renderStartMarkName)?.startTime ?? renderStartTime
    } catch {
      // Keep the performance.now() fallback when marking is unavailable.
    }

    try {
      const requestUrl = window.location.href
      collectRenderPerformanceStats = () => {
        sendCustomMetric({
          requestUrl,
          name: 'BROWSER_REACT_HYDRATION_DURATION',
          value: window.performance.now() - renderStartTime,
          tags: {
            appName,
            reactVersion: version,
            renderType,
            subAppName: getCurrentReactAppName() ?? appName,
            profilingEnabled: String(profiling.enabled),
            profilingSource: profiling.source,
          },
        })
      }
    } catch {
      // Browser timing and location access are best-effort.
    }
  }

  const recordRender = (stats: ReactRootRenderStats) => {
    if (didRecordRender) return
    didRecordRender = true
    sendStats({
      incrementKey: 'REACT_RENDER',
      incrementTags: {appName, ...stats},
    })
  }

  const onCommit = () => {
    if (didCommit) return
    didCommit = true

    try {
      performance.mark(commitMarkName)
    } catch {
      // Measures below safely tolerate a missing commit mark.
    }
    safeMeasureWithStartMark(
      `react-root:commit(${appName})`,
      renderStartMarkName,
      reactTrackDetail(`Commit (${appName})`, 'secondary'),
    )
    safeMeasureWithStartMark(`react-root:total(${appName})`, rootStartMarkName, reactTrackDetail(`Total (${appName})`))

    try {
      collectRenderPerformanceStats()
    } catch {
      // Instrumentation failures must not affect the committed application.
    }
  }

  return {startRender, recordRender, onCommit}
}

/** Runs a callback after the containing React root's initial commit without adding DOM. */
export function ReactRootCommit({children, onCommit}: PropsWithChildren<{onCommit: () => void}>) {
  const handleOnCommit = useEffectEvent(onCommit)

  useEffect(() => {
    handleOnCommit()
  }, [])

  return children
}
